import type { ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './auth-provider';

import { ConfirmProvider } from './confirm-provider';
import { ModalProvider } from './modal-provider';
import { createMutationCache } from '../lib/mutation-cache';

// Thunk breaks the circular reference: cache is created before client,
// but only calls getQueryClient() lazily when a mutation settles.
const mutationCache = createMutationCache(() => queryClient);

const queryClient: QueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Firestore real-time hooks manage their own freshness via onSnapshot.
      // Non-realtime queries (e.g. one-time fetches) get a 30s stale window.
      staleTime: 30_000,
      retry: 1,
    },
  },
  mutationCache,
});

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * App Providers Composition
 *
 * Composes all context providers in the correct order.
 * Order matters for provider dependencies:
 * 1. QueryClientProvider (TanStack Query — server state cache)
 * 2. AuthProvider (authentication state)
 * 3. ConfirmProvider (confirmation dialogs)
 * 4. ModalProvider (shared modals)
 *
 * @example
 * ```tsx
 * import { AppProviders } from '@app/providers';
 *
 * function App() {
 *   return (
 *     <AppProviders>
 *       <RouterProvider router={router} />
 *     </AppProviders>
 *   );
 * }
 * ```
 */
export function AppProviders({ children }: AppProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ConfirmProvider>
          <ModalProvider>{children}</ModalProvider>
        </ConfirmProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
